import {createSearchParams, useParams} from 'react-router-dom'
import {deleteRequest, getRequest} from '../../../services/base'
import Button from '../../common/Button'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../../context/global_context'
import {InlineConnectionContext, SELECT_INLINE_CONNECTION, SET_INLINE_CONNECTION_LIST,} from './context/context'
import InputText from '../../common/InputText'
import InlineConnectionFormModal from './form'
import InlineConnectionTable from './InlineConnectionTable'
import DownloadInlines from './DownloadInlines'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {Flex, Form, Modal, Row, Space} from 'antd'
import useDebouncedValue from '../../../utils/common_function'
import {useIntl} from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const InlineConnectionList = () => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(InlineConnectionContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef(`/api/inline_connection/?vehicle=${vehicle}`)

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header:
          intl.formatMessage({ id: 'layout.vehicleList' }) +
          ` / ${vehicle} / ` +
          intl.formatMessage({ id: 'sidebar.inlines' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getInlineConnectionsData()
  }, [])

  useEffect(() => {
    currentUrl.current = `/api/inline_connection/?vehicle=${vehicle}&search=${search}`
    getInlineConnectionsData()
  }, [debouncedSearch])

  const getInlineConnectionsData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'inlineConnections.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_INLINE_CONNECTION_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: {
        showLoader: false,
      },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'inlineConnections.loadError' }))
    }
  }

  const buildUrl = ({ vehicle, search, limit, offset }) => {
    const params = new URLSearchParams({
      vehicle,
      search,
      limit,
      offset,
    })
    return `/api/inline_connection/?${params.toString()}`
  }

  const deleteInlineConnection = async (inline_connection_id) => {
    let res = await deleteRequest(
      `/api/inline_connection/${inline_connection_id}/?vehicle=${vehicle}`
    )
    const { currentPage, totalCount, totalPages } = pagination
    const pageSize = 10
    const newTotalCount = totalCount - 1
    const newTotalPages = Math.max(1, Math.ceil(newTotalCount / pageSize))
    const pageToFetch = Math.min(currentPage, newTotalPages)
    const offset = (pageToFetch - 1) * pageSize

    getInlineConnectionsData(
      buildUrl({
        vehicle: vehicle,
        search: search,
        limit: pageSize,
        offset: offset,
      })
    )
    navDispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: intl.formatMessage({ id: 'inlineConnections.deleteSuccess' }),
      },
    })
  }

  const confirmDeleteInlineConnection = (inline_connection_id) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'inlineConnections.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({ id: 'inlineConnections.confirmDeleteContent' }),
      onOk: () => {
        deleteInlineConnection(inline_connection_id)
      },
      centered: true,
      maskClosable: true,
    })
  }

  const onEditInlineConnection = (record) => {
    dispatch({
      type: SELECT_INLINE_CONNECTION,
      data: { selected_inline_connection: record, show_inline_popup: true },
    })
  }

  const onCreateInlineConnection = () => {
    dispatch({
      type: SELECT_INLINE_CONNECTION,
      data: { selected_inline_connection: {}, show_inline_popup: true },
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.inline_connection_list?.length) return []

    return state?.inline_connection_list?.map((inline_connection, index) => {
      return {
        ...inline_connection,
        key: `inline-connection-${index}`,
        rowKey: `inline-connection-${inline_connection.id}`,
        index: index,
        from_connector_name: inline_connection.from_id?.name,
        to_connector_name: inline_connection.to_id?.name,
      }
    })
  }, [state.inline_connection_list])

  return (
    <>
      {contextHolder}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_inline_connections"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'inlineConnections.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                wrapperClass="search-inline-connections"
                onChange={(value) => {
                  setSearch(value)
                }}
              />

              <Space size="middle" align="center">
                <DownloadInlines />
                <Button onClick={onCreateInlineConnection}>
                  {intl.formatMessage({ id: 'inlineConnections.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <InlineConnectionTable
              dataSource={dataSource}
              pagination={pagination}
              getInlineConnectionsData={getInlineConnectionsData}
              onEdit={onEditInlineConnection}
              onDelete={confirmDeleteInlineConnection}
            />
          </Flex>
        </Row>
      </div>

      {state.show_inline_popup && (
        <InlineConnectionFormModal
          getListData={() => {
            getInlineConnectionsData()
          }}
        />
      )}
    </>
  )
}

export default InlineConnectionList
